import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Briefcase, Building, Percent, Calendar, ChevronRight, User,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { cn } from '../../utils/tailwindUtils';


const STATUS_BADGE = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  expired: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Math.ceil((d - new Date()) / 86400000);
}


export default function CorporateAccountsList() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiFetch('engage/corporate-accounts/');
        if (!cancelled) setAccounts(resp?.results || resp?.data?.results || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => accounts.filter(a => {
    if (!search) return true;
    const q = search.trim().toLowerCase();
    return [a.company_name, a.primary_contact_name, a.gstin].filter(Boolean).join(' ').toLowerCase().includes(q);
  }), [accounts, search]);

  return (
    <div className="p-4 max-w-[1600px] mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white shadow-sm">
            <Briefcase className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Corporate Accounts</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {loading ? 'Loading…' : `${filtered.length} accounts`}
            </p>
          </div>
        </div>
        <button onClick={() => navigate('/engage/corporate-accounts/new')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-sm shadow-sm hover:bg-primary/90">
          <Plus className="w-3.5 h-3.5" /> Add Account
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search company name or contact…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900 dark:text-white" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">Loading…</div>
        : filtered.length === 0 ? <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">No accounts yet.</div>
        : filtered.map(a => {
          const daysToExpiry = daysUntil(a.contract_end_date);
          const expiringSoon = daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 90;
          return (
            <button key={a.id} onClick={() => navigate(`/engage/corporate-accounts/${a.id}`)}
              className="group text-left bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-teal-300 dark:hover:border-teal-700 transition-all">
              <div className="flex items-start gap-2.5 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white shadow-sm">
                  <Building className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">{a.company_name}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', STATUS_BADGE[a.status] || STATUS_BADGE.inactive)}>
                      {a.status || 'active'}
                    </span>
                    {expiringSoon && (
                      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                        Renew in {daysToExpiry}d
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                {a.primary_contact_name && <div className="flex items-center gap-1.5"><User className="w-3 h-3" /> {a.primary_contact_name}</div>}
                {a.discount_pct != null && <div className="flex items-center gap-1.5"><Percent className="w-3 h-3" /> {a.discount_pct}% discount</div>}
                {a.contract_end_date && <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Ends {fmt(a.contract_end_date)}</div>}
              </div>
              <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-teal-500 transition-colors" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
